function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { EventTarget } from 'event-target-shim';
import memoizeClear from "./memoizeClear.js";
import GridUtils from "./GridUtils.js";
var LINK_TRUNCATION_LENGTH = 5000;

/* eslint class-methods-use-this: "off" */
/* eslint no-unused-vars: "off" */
/**
 * Model for a Grid
 * All of these methods should return very quickly, as they will be called many times in the render cycle.
 * If data needs to be loaded asynchronously, return something immediately, then trigger an event for the table to refresh (Not yet implemented).
 */
class GridModel extends EventTarget {
  constructor() {
    super(...arguments);
    _defineProperty(this, "getCachedTokensInText", memoizeClear((text, visibleLength) => {
      // If no text is truncated, then directly search in text
      if (visibleLength >= text.length) {
        return GridUtils.findTokensWithProtocolInText(text);
      }

      // To check for links, we should check to the first space after the truncatedText length
      var indexOfProceedingWhitespace = text.slice(visibleLength - 1, LINK_TRUNCATION_LENGTH).search(/\s/); // index or -1 if not found

      var lengthOfContent = visibleLength + indexOfProceedingWhitespace;
      // If it doesn't exist, set lengthOfContent to the minimum between length of the original text and 5000
      if (indexOfProceedingWhitespace === -1) {
        lengthOfContent = Math.min(LINK_TRUNCATION_LENGTH, text.length);
      }
      var contentToCheckForLinks = text.substring(0, lengthOfContent);
      return GridUtils.findTokensWithProtocolInText(contentToCheckForLinks);
    }, {
      max: 10000
    }));
  }
  /** Count of rows in the grid */
  /** Count of columns in the grid */
  /** Count of rows that are frozen (or 'floating') at the top */
  get floatingTopRowCount() {
    return 0;
  }

  /** Count of rows that are frozen at the bottom */
  get floatingBottomRowCount() {
    return 0;
  }

  /** Count of columns that are frozen (or 'floating') at the left */
  get floatingLeftColumnCount() {
    return 0;
  }

  /** Count of columns that are frozen (or 'floating') at the right */
  get floatingRightColumnCount() {
    return 0;
  }

  /**
   * How many columns header levels are in the grid
   * Used for column grouping where columns at depth 0 are the base columns
   *
   * A grid with 1-level grouping would have a columnHeaderDepth of 2
   * and column headers at depths 0 and 1
   */
  get columnHeaderMaxDepth() {
    return 1;
  }

  /**
   * Get the text for the specified cell
   * @param column Column to get the text for
   * @param row Row to get the text for
   * @returns Text for the specified cell
   */

  /**
   * Get the character to replace text when truncated for a specific cell.
   * Leave undefined to show text truncated with ellipsis
   * @param column Column to get the truncation character for
   * @param row Row to get the truncation character for
   * @returns Truncation character for the specified cell
   */
  truncationCharForCell(column, row) {
    return undefined;
  }

  /**
   * Get the text alignment for the specified cell
   * @param column Column to get the alignment for
   * @param row Row to get the alignment for
   * @returns Text alignment for the specified cell
   */
  textAlignForCell(column, row) {
    return 'left';
  }

  /**
   * Get the color for the text in the specified cell
   * @param column Column to get the color for
   * @param row Row to get the color for
   * @param theme Theme applied to the grid
   * @returns Color for the text in the cell
   */
  colorForCell(column, row, theme) {
    return theme.textColor;
  }

  /**
   * Get the background color for the cell
   * @param column Column to get the background color for
   * @param row Row to get the background color for
   * @param theme Theme applied to the grid
   * @returns Background color for the cell
   */
  backgroundColorForCell(column, row, theme) {
    return null;
  }

  /**
   * Text for the column header
   * @param column Column to get the header for
   * @param depth Depth to get the header text for. 0 is base columns
   * @returns Text to put in the column header
   */

  /** Color for column header
   * @param column Column to get the color for
   * @param depth Header depth to get the color for
   * @returns Color for the header at the depth or null
   */
  colorForColumnHeader(column) {
    var depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    return null;
  }

  /**
   * Text for the row header
   * @param row Row to get the header for
   * @returns Text to put in the row header
   */
  textForRowHeader(row) {
    return '';
  }

  /**
   * Text for the row footer
   * @param row Row to get the footer for
   * @returns Text to put in the row footer
   */
  textForRowFooter(row) {
    return '';
  }

  /**
   * @param column Column to check
   * @returns True if the column is movable
   */
  isColumnMovable(column) {
    var depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    return true;
  }

  /**
   * @param row Row to check
   * @returns True if the row is movable
   */
  isRowMovable(row) {
    return true;
  }
  getColumnHeaderGroup(modelIndex, depth) {
    return undefined;
  }
  getColumnHeaderParentGroup(modelIndex, depth) {
    return undefined;
  }

  /**
   * Gets the tokens in the cell at column and row, based on the visible text
   * @param column The model column
   * @param row The model row
   * @param visibleLength The length of the visible text
   * @returns An array of Tokens in the cell
   */
  tokensForCell(column, row) {
    var visibleLength = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : LINK_TRUNCATION_LENGTH;
    var text = this.textForCell(column, row);
    return this.getCachedTokensInText(text, visibleLength);
  }
  renderTypeForCell(column, row) {
    return 'text';
  }

  /**
   * Get the column restrictions for a column, which can be used to determine
   * what type of input component to render for cells in that column.
   * @param column The model index of the column
   * @returns The column restrictions, or an empty array if there are none
   */
  getColumnRestrictions(column) {
    return [];
  }
}

/**
 * Base type for a column restriction. Carries a `type` discriminator that
 * can be used to narrow to a more specific restriction shape.
 */

export default GridModel;
//# sourceMappingURL=GridModel.js.map